import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'

import Slider from "react-slick";

import bg1 from '../../assets/img/p-1.jpg'
import bg2 from '../../assets/img/p-2.jpg'
import bg3 from '../../assets/img/p-3.jpg'
import bg4 from '../../assets/img/p-4.jpg'
import bed from '../../assets/img/bed.svg'
import bathtub from '../../assets/img/bathtub.svg'
import move from '../../assets/img/move.svg'

import Navbar from '../../components/navbar/navbar'
import PropertyDetail from '../../components/property-detail'
import DetailSidebarTwo from '../../components/detail-sidebar-two'
import Footer from '../../components/footer'

import { API_BASE, getImageUrl } from '../../utils/api'

interface DBAgent {
    _id: string; name: string; email: string; role: string; phone?: string;
    about?: string; city?: string; state?: string; avatar?: string;
}

interface DBProperty {
    _id: string; title: string; price: number; status: string; type: string;
    city?: string; address?: string; bedrooms?: number; bathrooms?: number; area?: string;
    images?: string[]; isActive: boolean; isFeatured?: boolean; createdAt: string;
    description?: string; shortOverview?: string; features?: string[]; buildingAge?: string; garage?: string; rooms?: string;
    owner?: DBAgent;
    brochureUrl?: string;
    brochureName?: string;
    legalDocUrl?: string;
    legalDocName?: string;
    nearbyLocations?: {
        name: string;
        km: string;
        imageUrl: string;
    }[];
    floorPlans?: {
        name: string;
        size: string;
        description: string;
        image: string;
    }[];
    videoUrl?: string;

    // Advanced Details
    subCategory?: string;
    furnishing?: string;
    totalFloors?: number;
    propertyFloor?: number;
    totalRooms?: number;
    facing?: string;
    sellType?: string;
    constructionStatus?: string;
    reraRegistered?: boolean;
    reraNumber?: string;
    balcony?: number;
    liftAvailable?: boolean;
    powerBackup?: boolean;
    societyName?: string;
    gatedCommunity?: boolean;
    cornerProperty?: boolean;
    parkFacing?: boolean;
    roadWidth?: string;
    nearbyMetro?: boolean;
    parkingType?: string;
    totalParking?: number;
}

interface OtpModalState {
    open: boolean
    target: 'brochure' | 'legal' | null
    fileUrl: string
    fileName: string
    step: 'info' | 'otp'
    name: string
    email: string
    mobile: string
    otp: string
    loading: boolean
    error: string
    success: string
}

var settings = {
    dots: false,
    slidesToShow: 2,
    infinite: true,
    autoplay: true,
    autoplaySpeed: 2000,
    speed: 3000,
    slidesToScroll: 1,
    centerMode: true,
};

export default function SinglePropertyOne() {
    const { id } = useParams<{ id: string }>()
    const [property, setProperty] = useState<DBProperty | null>(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [activeTab, setActiveTab] = useState('detail-features')

    const [otpModal, setOtpModal] = useState<OtpModalState>({
        open: false, target: null, fileUrl: '', fileName: '',
        step: 'info', name: '', email: '', mobile: '', otp: '',
        loading: false, error: '', success: ''
    })

    useEffect(() => {
        const handleScroll = () => {
            const sections = [
                'detail-features', 'description', 'amenities',
                'property-video', 'floor-plan', 'location', 'nearby-locations', 'documents', 'gallery', 'reviews'
            ]
            let current = 'detail-features'
            const scrollPosition = window.scrollY + 160
            for (const section of sections) {
                const element = document.getElementById(section)
                if (element && element.offsetTop <= scrollPosition) current = section
            }
            setActiveTab(current)
        }
        window.addEventListener('scroll', handleScroll)
        return () => window.removeEventListener('scroll', handleScroll)
    }, [])

    useEffect(() => {
        if (!id) return;
        setLoading(true)
        fetch(`${API_BASE}/properties/${id}`)
            .then(res => { if (!res.ok) throw new Error('Property not found'); return res.json() })
            .then(data => setProperty(data))
            .catch(err => { console.error(err); setError(err.message) })
            .finally(() => setLoading(false))
    }, [id])

    // --- OTP Modal handlers ---
    const openDownloadModal = (target: 'brochure' | 'legal', fileUrl: string, fileName: string) => {
        if (!fileUrl) return
        setOtpModal({
            open: true, target, fileUrl, fileName,
            step: 'info', name: '', email: '', mobile: '', otp: '',
            loading: false, error: '', success: ''
        })
    }
    const closeDownloadModal = () => setOtpModal(p => ({ ...p, open: false }))

    const handleSendOtp = async () => {
        if (!otpModal.name || !otpModal.email || !otpModal.mobile) {
            setOtpModal(p => ({ ...p, error: 'Please fill all fields.' })); return
        }
        setOtpModal(p => ({ ...p, loading: true, error: '' }))
        try {
            const res = await fetch(`${API_BASE}/properties/send-otp`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: otpModal.email })
            })
            const data = await res.json()
            if (res.ok) {
                setOtpModal(p => ({ ...p, loading: false, step: 'otp', success: 'OTP sent to your email!' }))
            } else {
                setOtpModal(p => ({ ...p, loading: false, error: data.message || 'Failed to send OTP.' }))
            }
        } catch {
            setOtpModal(p => ({ ...p, loading: false, error: 'Server error. Please try again.' }))
        }
    }

    const handleVerifyAndDownload = async () => {
        if (!otpModal.otp) { setOtpModal(p => ({ ...p, error: 'Please enter the OTP.' })); return }
        setOtpModal(p => ({ ...p, loading: true, error: '' }))
        try {
            const res = await fetch(`${API_BASE}/properties/verify-otp`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email: otpModal.email, otp: otpModal.otp })
            })
            const data = await res.json()
            if (res.ok && data.success) {
                const link = document.createElement('a')
                link.href = otpModal.fileUrl
                link.download = otpModal.fileName
                link.target = '_blank'
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                setOtpModal(p => ({ ...p, loading: false, open: false }))
            } else {
                setOtpModal(p => ({ ...p, loading: false, error: data.message || 'Incorrect OTP.' }))
            }
        } catch {
            setOtpModal(p => ({ ...p, loading: false, error: 'Server error. Please try again.' }))
        }
    }

    if (loading) {
        return (
            <>
                <Navbar transparent={false} />
                <div className="container py-5 text-center">
                    <div className="spinner-border text-primary" role="status"></div>
                    <p className="mt-3">Loading property details...</p>
                </div>
                <Footer />
            </>
        )
    }

    if (error || !property) {
        return (
            <>
                <Navbar transparent={false} />
                <div className="container py-5 text-center">
                    <i className="fa-solid fa-circle-exclamation fa-4x text-danger mb-4"></i>
                    <h2>Oops! Property Not Found</h2>
                    <p className="text-muted">The property you are looking for might have been removed or is unavailable.</p>
                    <Link to="/property" className="btn btn-primary mt-3">Back to Properties</Link>
                </div>
                <Footer />
            </>
        )
    }

    const hasBrochure = Boolean(property.brochureUrl)
    const hasLegalDoc = Boolean(property.legalDocUrl)

    return (
        <>
            <Navbar transparent={false} />

            <div className="featured_slick_gallery gray">
                <div className="featured_slick_gallery-slide home-slider">
                    <Slider {...settings}>
                        {property.images && property.images.length > 0 ? (
                            property.images.map((img, index) => (
                                <div className="featured_slick_padd" key={index}>
                                    <a href={getImageUrl(img)} className="mfp-gallery">
                                        <img src={getImageUrl(img)} className="img-fluid mx-auto" alt="" />
                                    </a>
                                </div>
                            ))
                        ) : (
                            <>
                                <div className="featured_slick_padd"><a href={bg1} className="mfp-gallery"><img src={bg1} className="img-fluid mx-auto" alt="" /></a></div>
                                <div className="featured_slick_padd"><a href={bg2} className="mfp-gallery"><img src={bg2} className="img-fluid mx-auto" alt="" /></a></div>
                                <div className="featured_slick_padd"><a href={bg3} className="mfp-gallery"><img src={bg3} className="img-fluid mx-auto" alt="" /></a></div>
                                <div className="featured_slick_padd"><a href={bg4} className="mfp-gallery"><img src={bg4} className="img-fluid mx-auto" alt="" /></a></div>
                            </>
                        )}
                    </Slider>
                </div>
                <Link to="#" className="btn-view-pic">View photos</Link>
            </div>

            {/* Sticky Tab Navigation */}
            <div className="property-nav sticky-top border-bottom shadow-sm" style={{ zIndex: 1020, top: '130px', backgroundColor: '#f4f0e9', marginTop: '40px' }}>
                <div className="container">
                    <ul className="nav nav-pills d-flex overflow-auto flex-nowrap py-3" style={{ whiteSpace: 'nowrap', gap: '10px' }}>
                        <li className="nav-item">
                            <a className={`nav-link fw-bold px-3 py-2 rounded${activeTab === 'detail-features' ? ' active-tab' : ''}`} href="#detail-features">Detail &amp; Features</a>
                        </li>
                        <li className="nav-item">
                            <a className={`nav-link fw-bold px-3 py-2 rounded${activeTab === 'description' ? ' active-tab' : ''}`} href="#description">Description</a>
                        </li>
                        <li className="nav-item">
                            <a className={`nav-link fw-bold px-3 py-2 rounded${activeTab === 'amenities' ? ' active-tab' : ''}`} href="#amenities">Amenities</a>
                        </li>
                        <li className="nav-item">
                            <a className={`nav-link fw-bold px-3 py-2 rounded${activeTab === 'property-video' ? ' active-tab' : ''}`} href="#property-video">Video</a>
                        </li>
                        <li className="nav-item">
                            <a className={`nav-link fw-bold px-3 py-2 rounded${activeTab === 'floor-plan' ? ' active-tab' : ''}`} href="#floor-plan">Floor Plan</a>
                        </li>
                        <li className="nav-item">
                            <a className={`nav-link fw-bold px-3 py-2 rounded${activeTab === 'location' ? ' active-tab' : ''}`} href="#location">Location</a>
                        </li>
                        <li className="nav-item">
                            <a className={`nav-link fw-bold px-3 py-2 rounded${activeTab === 'nearby-locations' ? ' active-tab' : ''}`} href="#nearby-locations">Nearby</a>
                        </li>
                        <li className="nav-item">
                            <a className={`nav-link fw-bold px-3 py-2 rounded${activeTab === 'documents' ? ' active-tab' : ''}`} href="#documents">Documents</a>
                        </li>
                        <li className="nav-item">
                            <a className={`nav-link fw-bold px-3 py-2 rounded${activeTab === 'gallery' ? ' active-tab' : ''}`} href="#gallery">Gallery</a>
                        </li>
                        <li className="nav-item">
                            <a className={`nav-link fw-bold px-3 py-2 rounded${activeTab === 'reviews' ? ' active-tab' : ''}`} href="#reviews">Reviews</a>
                        </li>
                    </ul>
                </div>
            </div>

            <section className="gray-simple">
                <div className="container">
                    <div className="row">
                        {/* Property Detail Content */}
                        <div className="col-lg-8 col-md-12 col-sm-12">
                            <div className="property_block_wrap style-2 p-4">
                                <h4 className="property_block_title mb-4">Overview</h4>
                                <div className="prt-detail-title-desc">
                                    <span className={`prt-types ${property.status === 'For Sale' ? 'sale' : 'rent'}`}>{property.status}</span>
                                    <h3 className='mt-3 fw-bold' style={{ fontSize: '30px', color: '#0a1f6e' }}>{property.title}</h3>
                                    <span style={{ fontSize: '15px' }}><i className="lni-map-marker"></i> {property.address}{property.address && property.city ? ', ' : ''}{property.city}</span>
                                    <h3 className="prt-price-fix text-primary mt-3" style={{ fontSize: '26px' }}>₹ {property.price ? property.price.toLocaleString() : '0'}<span>{property.status === 'For Rent' ? '/mo' : ''}</span></h3>

                                    {/* Short About Section */}
                                    {(property.shortOverview || property.description) && (
                                        <div className="mt-4 p-3 rounded" style={{ backgroundColor: '#f8f9fa', borderLeft: '4px solid #0a1f6e' }}>
                                            <h6 className="fw-bold mb-2" style={{ color: '#0a1f6e' }}>Short About Property</h6>
                                            <p className="mb-0 text-muted" style={{ fontSize: '14px', lineHeight: '1.6' }}>
                                                {property.shortOverview || (property.description && (property.description.length > 280
                                                    ? `${property.description.substring(0, 280)}...`
                                                    : property.description))}
                                            </p>
                                        </div>
                                    )}

                                    <div className="list-fx-features mt-4 d-flex align-items-center gap-4">
                                        <div className="listing-card-info-icon" style={{ fontSize: '17px', gap: '8px', display: 'flex', alignItems: 'center' }}>
                                            <div className="inc-fleat-icon me-1"><img src={bed} width="22" alt="" /></div>{property.bedrooms || 0} Beds
                                        </div>
                                        <div className="listing-card-info-icon" style={{ fontSize: '17px', gap: '8px', display: 'flex', alignItems: 'center' }}>
                                            <div className="inc-fleat-icon me-1"><img src={bathtub} width="22" alt="" /></div>{property.bathrooms || 0} Bath
                                        </div>
                                        <div className="listing-card-info-icon" style={{ fontSize: '17px', gap: '8px', display: 'flex', alignItems: 'center' }}>
                                            <div className="inc-fleat-icon me-1"><img src={move} width="22" alt="" /></div>{property.area || '0'} sqft
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <PropertyDetail item={property} propertyId={property._id} />

                            {/* ─── Nearby Locations Section ─── */}
                            <div className="property_block_wrap style-2 p-4 mt-4" id="nearby-locations">
                                <div className="property_block_wrap_header mb-3">
                                    <h4 className="property_block_title" style={{ color: '#0a1f6e' }}>
                                        <i className="fa-solid fa-location-dot me-2" style={{ color: '#27ae60' }}></i>
                                        Nearby Locations
                                    </h4>
                                    <p className="text-muted mb-0" style={{ fontSize: '13px' }}>
                                        Key points of interest and distances from this property.
                                    </p>
                                </div>

                                {!property.nearbyLocations || property.nearbyLocations.length === 0 ? (
                                    <div className="p-4 text-center bg-light rounded" style={{ border: '1px dashed #ced4da' }}>
                                        <i className="fa-solid fa-map-location-dot fa-2x mb-2" style={{ color: '#adb5bd' }}></i>
                                        <p className="mb-0 text-muted">No nearby locations listed for this property.</p>
                                    </div>
                                ) : (
                                    <div className="row g-3">
                                        {property.nearbyLocations.map((loc, idx) => (
                                            <div className="col-md-6 col-lg-4" key={idx}>
                                                <div className="d-flex align-items-center p-3 h-100"
                                                    style={{ border: '1px solid #e0e0e0', borderRadius: '12px', background: '#fff', transition: 'transform 0.2s' }}>
                                                    <div className="me-3" style={{ flexShrink: 0 }}>
                                                        {loc.imageUrl ? (
                                                            <img src={getImageUrl(loc.imageUrl)} alt={loc.name} style={{ width: '56px', height: '56px', objectFit: 'cover', borderRadius: '8px' }} />
                                                        ) : (
                                                            <div style={{ width: '56px', height: '56px', borderRadius: '8px', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                                                <i className="fa-solid fa-location-arrow text-success fs-4"></i>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div style={{ overflow: 'hidden' }}>
                                                        <div className="fw-bold text-dark text-truncate" style={{ fontSize: '16px' }} title={loc.name}>{loc.name}</div>
                                                        <div className="text-success fw-semibold" style={{ fontSize: '14px' }}>
                                                            <i className="fa-solid fa-person-walking me-1"></i>
                                                            {loc.km} KM Away
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>

                            {/* ─── Project Brochure Section ─── */}
                            <div className="property_block_wrap style-2 mt-4" id="documents"
                                style={{ background: '#faf8f5', borderRadius: '16px', padding: '40px 24px', textAlign: 'center' }}>
                                <h3 style={{ color: '#b8965a', fontStyle: 'italic', fontWeight: 600, fontSize: '28px', marginBottom: '10px' }}>
                                    Project Brochure
                                </h3>
                                <p className="text-muted" style={{ fontSize: '15px', marginBottom: '24px' }}>
                                    Explore features, amenities and more in our brochure
                                </p>
                                {hasBrochure ? (
                                    <button type="button" className="btn fw-bold px-5 py-2"
                                        onClick={() => openDownloadModal('brochure', property.brochureUrl!, property.brochureName || 'brochure.pdf')}
                                        style={{
                                            background: 'linear-gradient(135deg, #c9a96e, #b8965a)',
                                            color: '#fff', borderRadius: '8px', fontSize: '16px',
                                            border: 'none', letterSpacing: '0.5px'
                                        }}>
                                        Download Brochure
                                    </button>
                                ) : (
                                    <p className="text-muted mb-0" style={{ fontSize: '14px' }}>
                                        <i className="fa-solid fa-file-circle-xmark me-1"></i> No brochure available for this property.
                                    </p>
                                )}

                            </div>
                        </div>

                        {/* Sidebar */}
                        <div className="col-lg-4 col-md-12 col-sm-12">
                            <DetailSidebarTwo
                                agent={property.owner}
                                propertyId={property._id}
                                brochureUrl={property.brochureUrl}
                                legalDocUrl={property.legalDocUrl}
                            />
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── OTP Download Modal ─── */}
            {otpModal.open && (
                <div className="modal show d-block" style={{ zIndex: 9999, background: 'rgba(0,0,0,0.55)' }}>
                    <div className="modal-dialog modal-dialog-centered" style={{ maxWidth: '460px' }}>
                        <div className="modal-content" style={{ borderRadius: '16px', overflow: 'hidden', border: 'none' }}>
                            {/* Header */}
                            <div className="modal-header border-0" style={{ background: 'linear-gradient(135deg, #0a1f6e, #1a3a9e)', padding: '20px 24px' }}>
                                <div>
                                    <h5 className="modal-title text-white mb-1">
                                        <i className="fa-solid fa-shield-halved me-2"></i>
                                        Verify to Download
                                    </h5>
                                    <small style={{ color: 'rgba(255,255,255,0.7)' }}>
                                        {otpModal.target === 'brochure' ? '📄 Property Brochure' : '📋 Legal Document'}
                                    </small>
                                </div>
                                <button type="button" className="btn-close btn-close-white" onClick={closeDownloadModal}></button>
                            </div>

                            <div className="modal-body p-4">
                                {/* Step progress bar */}
                                <div className="d-flex mb-4" style={{ gap: '8px' }}>
                                    <div style={{ flex: 1, height: '4px', borderRadius: '4px', background: '#0a1f6e' }}></div>
                                    <div style={{ flex: 1, height: '4px', borderRadius: '4px', background: otpModal.step === 'otp' ? '#0a1f6e' : '#e9ecef' }}></div>
                                </div>

                                {otpModal.error && (
                                    <div className="alert alert-danger py-2 mb-3" style={{ fontSize: '13px', borderRadius: '8px' }}>
                                        <i className="fa-solid fa-circle-exclamation me-1"></i>{otpModal.error}
                                    </div>
                                )}
                                {otpModal.success && (
                                    <div className="alert alert-success py-2 mb-3" style={{ fontSize: '13px', borderRadius: '8px' }}>
                                        <i className="fa-solid fa-check-circle me-1"></i>{otpModal.success}
                                    </div>
                                )}

                                {otpModal.step === 'info' ? (
                                    <>
                                        <p className="text-muted mb-3" style={{ fontSize: '14px' }}>
                                            Please fill in your details. We'll send an OTP to verify your identity.
                                        </p>
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>
                                                <i className="fa-solid fa-user me-1 text-primary"></i> Full Name
                                            </label>
                                            <input type="text" className="form-control" placeholder="Enter your full name"
                                                value={otpModal.name}
                                                onChange={e => setOtpModal(p => ({ ...p, name: e.target.value }))} />
                                        </div>
                                        <div className="mb-3">
                                            <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>
                                                <i className="fa-solid fa-envelope me-1 text-primary"></i> Email Address
                                            </label>
                                            <input type="email" className="form-control" placeholder="Enter your email"
                                                value={otpModal.email}
                                                onChange={e => setOtpModal(p => ({ ...p, email: e.target.value }))} />
                                        </div>
                                        <div className="mb-4">
                                            <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>
                                                <i className="fa-solid fa-phone me-1 text-primary"></i> Mobile Number
                                            </label>
                                            <input type="tel" className="form-control" placeholder="Enter your mobile number"
                                                value={otpModal.mobile}
                                                onChange={e => setOtpModal(p => ({ ...p, mobile: e.target.value }))} />
                                        </div>
                                        <button type="button" className="btn w-100 fw-bold" onClick={handleSendOtp}
                                            disabled={otpModal.loading}
                                            style={{ background: '#0a1f6e', color: '#fff', borderRadius: '10px', padding: '12px' }}>
                                            {otpModal.loading
                                                ? <><span className="spinner-border spinner-border-sm me-2"></span>Sending OTP...</>
                                                : <><i className="fa-solid fa-paper-plane me-2"></i>Send OTP</>}
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <p className="text-muted mb-3" style={{ fontSize: '14px' }}>
                                            Enter the 6-digit OTP sent to <strong>{otpModal.email}</strong>
                                        </p>
                                        <div className="mb-4">
                                            <label className="form-label fw-semibold" style={{ fontSize: '13px' }}>
                                                <i className="fa-solid fa-key me-1 text-primary"></i> Enter OTP
                                            </label>
                                            <input type="text" className="form-control text-center fw-bold"
                                                placeholder="_ _ _ _ _ _"
                                                maxLength={6}
                                                style={{ fontSize: '24px', letterSpacing: '10px', borderRadius: '10px', padding: '12px' }}
                                                value={otpModal.otp}
                                                onChange={e => setOtpModal(p => ({ ...p, otp: e.target.value }))} />
                                        </div>
                                        <button type="button" className="btn w-100 fw-bold mb-2" onClick={handleVerifyAndDownload}
                                            disabled={otpModal.loading}
                                            style={{ background: '#27ae60', color: '#fff', borderRadius: '10px', padding: '12px' }}>
                                            {otpModal.loading
                                                ? <><span className="spinner-border spinner-border-sm me-2"></span>Verifying...</>
                                                : <><i className="fa-solid fa-download me-2"></i>Verify &amp; Download</>}
                                        </button>
                                        <button type="button" className="btn w-100 btn-outline-secondary"
                                            style={{ borderRadius: '10px' }}
                                            onClick={() => setOtpModal(p => ({ ...p, step: 'info', otp: '', error: '', success: '' }))}>
                                            <i className="fa-solid fa-arrow-left me-1"></i> Back
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}


            <Footer />
        </>
    )
}
